/**
 * Store related to the steps process
 */

import { stepRoutes } from '@/steps-meta/routes'
import { finalizeString } from '@/util/helpers'
import type { RouteLocationNormalized } from 'vue-router'

export const useStepsStore = defineStore('steps', () => {
  const requestMetaStore = useRequestMetaStore()
  const router = useRouter()
  const currentRoute = useRoute()

  const activeStepRoutes = computed(() =>
    stepRoutes.filter(route => {
      return typeof route.meta.step.isActive === 'function'
        ? route.meta.step.isActive(route.meta.step.store, requestMetaStore)
        : route.meta.step.isActive
    }),
  )

  // The index of the current step
  const currentStepIndex = computed(() =>
    stepRoutes.findIndex(route => route.name === currentRoute.matched[0]?.name),
  )

  // The current step
  const currentStep = computed(() => stepRoutes[currentStepIndex.value])

  // Whether there is a step before the current one
  const hasPreviousStep = computed(() => currentStepIndex.value > 0)

  // The previous step
  const previousStep = computed(() => {
    if (!hasPreviousStep.value) return undefined
    return stepRoutes[currentStepIndex.value - 1]
  })

  // All previous steps
  const previousSteps = computed(() => stepRoutes.slice(0, currentStepIndex.value))

  // Go to the previous step (validation is ignored)
  function goPrevious() {
    if (!hasPreviousStep.value) return
    router.push(previousStep.value!.path)
  }

  // Whether there is a step after the current one
  const hasNextStep = computed(
    () =>
      currentStepIndex.value !== -1 && currentStepIndex.value < activeStepRoutes.value.length - 1,
  )

  // The next step
  const nextStep = computed(() => {
    if (!hasNextStep.value) return undefined
    return stepRoutes[currentStepIndex.value + 1]
  })

  // All following steps
  const nextSteps = computed(() => stepRoutes.slice(currentStepIndex.value + 1))

  // Go to the next step (validation is ignored)
  function goNext() {
    if (!hasNextStep.value) return
    router.push(nextStep.value!.path)
  }

  // Go to a specific step
  function goTo(index: number) {
    if (typeof stepRoutes[index] === 'undefined') return
    router.push(stepRoutes[index].path)
  }

  const attemptContinueHook = createEventHook<() => void>()

  // Attempt to go to a specific step (always works if target step is before current step,
  // but going going to a later step will trigger validation)
  function canGoTo(index: number): boolean {
    if (index > currentStepIndex.value) {
      let prevented = false
      attemptContinueHook.trigger(() => {
        prevented = true
      })

      if (prevented) return false
    }

    return true
  }

  // Attempt to go to the next step, triggers validation
  function canGoNext(): boolean {
    return canGoTo(currentStepIndex.value + 1)
  }

  function getIndex(route: RouteLocationNormalized) {
    return stepRoutes.findIndex(stepRoute => stepRoute.name === route.name)
  }

  return {
    getIndex,
    currentStepIndex,
    currentStep,
    hasPreviousStep,
    previousStep,
    previousSteps,
    goPrevious,
    hasNextStep,
    nextStep,
    nextSteps,
    goNext,
    goTo,
    canGoNext,
    canGoTo,
    onAttemptContinue: attemptContinueHook.on,
    getChildPath(path: string) {
      if (!currentStep.value) return undefined
      return finalizeString(currentStep.value.path, '/') + path
    },
  }
})
